export const API_BASE_URL =
  import.meta.env.VITE_BACKEND_URL || "http://localhost:3000";

const CLIENT_APP = "guest";

const RETRYABLE_HTTP_STATUS = new Set([502, 503, 504]);

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

const IS_ELECTRON =
  typeof window !== "undefined" && Boolean(window.electronAPI);

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
  ) {
    super(`HTTP ${status}: ${body}`);
    this.name = "HttpError";
  }
}

function getElectronAccessKey(): string | null {
  // Only consulted in Electron. The desktop app receives the accessKey via
  // custom-protocol deep link from the web SPA — that handoff happens out of
  // process so HttpOnly cookies can't bridge it. Until we add a proper
  // "upgrade-token-to-cookie" backend endpoint, Electron continues to use the
  // localStorage + Authorization header path the way it always has.
  if (!IS_ELECTRON) return null;
  return localStorage.getItem("@Guest/accessKey");
}

function readCsrfToken(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)eriol_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function isRetryableMethod(method: string): boolean {
  return method.toUpperCase() === "GET";
}

function isRetryableNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes("failed to fetch") ||
    msg.includes("fetch failed") ||
    msg.includes("networkerror")
  );
}

function getRetryDelayMs(attempt: number): number {
  return Math.min(300 * 2 ** (attempt - 1), 1500);
}

async function sleep(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

function buildHeaders(method: string, auth: boolean): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Client-App": CLIENT_APP,
  };

  if (auth) {
    // Browser path: rely on the HttpOnly cookie + CSRF token. Electron path:
    // keep the Authorization header until we wire the deep-link → cookie
    // bridge (see comment in getElectronAccessKey).
    const electronKey = getElectronAccessKey();
    if (electronKey) {
      headers["Authorization"] = `AccessKey ${electronKey}`;
    } else if (!SAFE_METHODS.has(method.toUpperCase())) {
      const csrf = readCsrfToken();
      if (csrf) headers["X-CSRF-Token"] = csrf;
    }
  } else if (!SAFE_METHODS.has(method.toUpperCase())) {
    // Even unauthenticated mutations (login, register) need the CSRF token if
    // the user happened to be logged in already — the middleware enforces it
    // whenever a session cookie is present on the request.
    const csrf = readCsrfToken();
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }

  return headers;
}

async function request<T>(
  method: string,
  endpoint: string,
  data?: unknown,
  auth = false,
): Promise<T> {
  const normalizedMethod = method.toUpperCase();

  // `credentials: "include"` ships the HttpOnly `eriol_session` cookie that
  // backend's /auth/login + /auth/register set in Phase 1. Backend CORS
  // already has Allow-Credentials: true. Harmless in Electron context.
  const opts: RequestInit = {
    method: normalizedMethod,
    headers: buildHeaders(normalizedMethod, auth),
    credentials: "include",
  };

  if (data) opts.body = JSON.stringify(data);

  const maxAttempts = isRetryableMethod(normalizedMethod) ? 4 : 1;
  const requestUrl = `${API_BASE_URL}${endpoint}`;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    let response: Response;

    try {
      response = await fetch(requestUrl, opts);
    } catch (error) {
      const shouldRetry =
        attempt < maxAttempts && isRetryableNetworkError(error);
      if (!shouldRetry) throw error;
      await sleep(getRetryDelayMs(attempt));
      continue;
    }

    if (!response.ok) {
      if (response.status === 401 && auth) {
        window.dispatchEvent(new CustomEvent("auth:session-expired"));
      }

      const text = await response.text();
      const shouldRetry =
        attempt < maxAttempts && RETRYABLE_HTTP_STATUS.has(response.status);

      if (shouldRetry) {
        await sleep(getRetryDelayMs(attempt));
        continue;
      }

      throw new HttpError(response.status, text);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  throw new Error(
    `Request failed after ${maxAttempts} attempts: ${normalizedMethod} ${endpoint}`,
  );
}

export const api = {
  get: <T>(endpoint: string) => request<T>("GET", endpoint),

  post: <T>(endpoint: string, data?: unknown) =>
    request<T>("POST", endpoint, data),

  put: <T>(endpoint: string, data: unknown) =>
    request<T>("PUT", endpoint, data),

  patch: <T>(endpoint: string, data?: unknown) =>
    request<T>("PATCH", endpoint, data),

  authGet: <T>(endpoint: string) =>
    request<T>("GET", endpoint, undefined, true),

  authPost: <T>(endpoint: string, data?: unknown) =>
    request<T>("POST", endpoint, data, true),

  authPut: <T>(endpoint: string, data: unknown) =>
    request<T>("PUT", endpoint, data, true),

  authPatch: <T>(endpoint: string, data?: unknown) =>
    request<T>("PATCH", endpoint, data, true),
};
